import React, { memo, useMemo, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io';
import { Flag, Loader2, MapPin, Shield, Trophy, User, UserRound } from 'lucide-react';
import brandMark from '../../assets/feedalogoWhite.svg';
import IntermediateStepsPanel from './ChatSearchSteps/IntermediateStepsPanel';
import MessageContent from './MessageContent';
import EntityActions from './EntityActions';
import { extractMcpEntities } from '../../helpers/mcpEntities';
import { buildHighlightedMarkdown } from '../../helpers/citations';
import {
  ENTITY_TYPES,
  buildHighlightedEntitiesMarkdown,
  extractEntities,
  extractToolResults,
} from '../../helpers/entities';

const SWIPER_MODULES = [Navigation];

const TYPE_ICONS = { player: User, coach: UserRound, referee: Flag, team: Shield, league: Trophy, venue: MapPin };

const toggleButtonClass = active =>
  `flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
    active
      ? 'border-transparent bg-[linear-gradient(135deg,#00c23e,#00dc49)] text-white'
      : 'border-shell-border bg-paper-2 text-mute hover:border-space/40 hover:text-ink'
  }`;

const ChatMessageItem = ({
  elem,
  ind,
  user,
  selectedAgent,
  teamDetails,
  sessionAgentIcon,
  selectedChatSources,
  onSelectSources,
  onSelectSource,
  onSelectEntity,
  isLast,
}) => {
  const [showSources, setShowSources] = useState(false);
  const [activeTypes, setActiveTypes] = useState({});

  const hasSources = (elem?.source?.length || 0) > 0;

  const entities = useMemo(() => extractEntities(elem?.intermediate_steps), [elem?.intermediate_steps]);
  const toolResults = useMemo(() => extractToolResults(elem?.intermediate_steps), [elem?.intermediate_steps]);
  const mcp = useMemo(() => extractMcpEntities(elem?.intermediate_steps), [elem?.intermediate_steps]);
  const presentTypes = useMemo(() => Object.keys(entities).filter(type => entities[type].length > 0), [entities]);
  const hasEntities = presentTypes.length > 0;

  const highlighted = useMemo(() => {
    if (!showSources || elem?.isStreaming) return { markdown: elem?.ai || '', citeById: {} };
    return buildHighlightedMarkdown(elem?.ai, elem?.source, elem?.images);
  }, [elem?.ai, elem?.source, elem?.images, elem?.isStreaming, showSources]);

  const entityHighlighted = useMemo(() => {
    const active = presentTypes.filter(type => activeTypes[type]);
    if (!active.length || elem?.isStreaming) return { markdown: highlighted.markdown, entityById: {} };
    return buildHighlightedEntitiesMarkdown(highlighted.markdown, entities, active);
  }, [highlighted.markdown, entities, presentTypes, activeTypes, elem?.isStreaming]);

  const toggleType = type => setActiveTypes(prev => ({ ...prev, [type]: !prev[type] }));

  return (
    <div className="min-w-0 max-w-full">
      {elem.human && (
        <div className="heading user-text">
          <div className="icon-box">{user?.first_name?.split('')[0]}</div>
          <strong className="title whitespace-pre-line">{elem?.human}</strong>
          {(elem?.source?.length > 0 || elem?.files?.length > 0) && (
            <button
              className="see-source-button"
              type="button"
              style={{ border: ind === selectedChatSources ? `2px solid var(--primary)` : undefined }}
              onClick={() => onSelectSources(ind)}>
              see sources
            </button>
          )}
        </div>
      )}

      <div className="heading">
        {selectedAgent?.mode === 'team' && elem?.delegate_agent ? (
          <div className="icon-box icon-box--purple">
            <img
              src={
                teamDetails?.members.find(agent => {
                  const normalizedName = agent?.agent_name.toLowerCase().replace(/\s+/g, '-');
                  return normalizedName === elem?.delegate_agent;
                })?.icon ?? brandMark
              }
              alt=""
            />
          </div>
        ) : (
          <div className="icon-box icon-box--purple">
            <img src={sessionAgentIcon ?? brandMark} alt="" />
          </div>
        )}

        {!elem?.delegate_agent && (
          <IntermediateStepsPanel steps={elem?.intermediate_steps} isLoading={elem?.isLoading} />
        )}

        {(hasSources || hasEntities) && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {hasSources && (
              <button type="button" onClick={() => setShowSources(v => !v)} className={toggleButtonClass(showSources)}>
                Sources
              </button>
            )}
            {presentTypes.map(type => {
              const Icon = TYPE_ICONS[type] || User;
              return (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleType(type)}
                  className={toggleButtonClass(activeTypes[type])}>
                  <Icon className="h-4 w-4" />
                  {ENTITY_TYPES[type]?.label}
                </button>
              );
            })}
          </div>
        )}

        {elem?.isLoading && !elem?.ai && (!elem?.intermediate_steps || elem?.intermediate_steps?.length === 0) && (
          <div className="flex items-center gap-2.5 py-2">
            <Loader2 className="w-4 h-4 animate-spin text-space" />
            <span className="text-sm text-mute">Thinking...</span>
          </div>
        )}

        {elem?.images && elem?.images?.length > 0 && elem?.images !== '' && (
          <div className="swiper-holder">
            <Swiper
              slidesPerView={4.2}
              spaceBetween={12}
              navigation={{
                prevEl: '.swiper-button-prev',
                nextEl: '.swiper-button-next',
              }}
              modules={SWIPER_MODULES}
              breakpoints={{
                320: { slidesPerView: 1.3, spaceBetween: 10 },
                574: { slidesPerView: 2.2, spaceBetween: 12 },
                1250: { slidesPerView: 2.8, spaceBetween: 12 },
                1440: { slidesPerView: 3.2, spaceBetween: 14 },
                1750: { slidesPerView: 4.2, spaceBetween:14 },
              }}>
              {elem?.images?.map((data, index) => (
                <SwiperSlide key={index}>
                  <a href={data?.origin_url} target="_blank" rel="noreferrer" className="img-wrap">
                    <img src={data?.image_url} alt="img" loading="lazy" />
                  </a>
                </SwiperSlide>
              ))}
            </Swiper>
            <div className="swiper-controls">
              <button type="button" className="swiper-button-prev">
                <IoIosArrowBack />
              </button>
              <button type="button" className="swiper-button-next">
                <IoIosArrowForward />
              </button>
            </div>
          </div>
        )}

        {elem.ai && elem.ai !== '' && (
          <div className="ai-message-content">
            <MessageContent
              citeById={highlighted.citeById}
              entityById={entityHighlighted.entityById}
              toolResults={toolResults}
              onSelectSource={onSelectSource}
              onSelectEntity={onSelectEntity}>
              {entityHighlighted.markdown}
            </MessageContent>
          </div>
        )}

        {!elem?.isLoading && !elem?.isStreaming && (
          <EntityActions entities={mcp.entities} eventRecords={mcp.eventRecords} venueRecords={mcp.venueRecords} />
        )}

        {!elem.ai &&
          !elem?.isLoading &&
          (user?.usage?.api_calls >= user?.plan?.request_limit ? (
            <span className="error-text">❌ Limit exceeded. Please upgrade your plan.</span>
          ) : (
            <span className="error-text">Something went wrong !!!</span>
          ))}
      </div>
    </div>
  );
};

export default memo(ChatMessageItem, (prev, next) => {
  if (next.isLast) return false;

  return (
    prev.elem === next.elem &&
    prev.ind === next.ind &&
    prev.selectedChatSources === next.selectedChatSources &&
    prev.sessionAgentIcon === next.sessionAgentIcon &&
    prev.teamDetails === next.teamDetails
  );
});
